import './styles.scss';

import { DirectionsRenderer, DirectionsService, InfoWindow } from '@react-google-maps/api';
import React, { useMemo } from 'react';

import { Card } from 'react-bootstrap';
import CardHeader from '@/features/DriveTimeTool/components/CardHeader';
import MapDisplay from '@/components/MapDisplay';
import MapMarker from '@/components/MapDisplay/MapMarker';
import { Styles } from '@/models';
import { createComponentState } from '@/state';
import { useState } from 'react';

export type RouteMapState = {
  routes: Record<string, google.maps.DirectionsResult>;
  puAddress: string;
  doAddress: string;
};

export const useRouteMap = createComponentState<RouteMapState>({
  routes: {},
  puAddress: '',
  doAddress: '',
});

const RouteMap = (): JSX.Element => {
  const [{ route, puAddress, doAddress }, setRoute] = useRouteMap(({ state, setState }) => [
    {
      route: state.routes[`${state.puAddress}::${state.doAddress}`],
      puAddress: state.puAddress,
      doAddress: state.doAddress,
    },
    (res: google.maps.DirectionsResult) => {
      setState((prevState) => ({
        ...prevState,
        routes: {
          ...prevState.routes,
          [`${state.puAddress}::${state.doAddress}`]: res,
        },
      }));
    },
  ]);
  const [travelMode, setTravelMode] = useState<google.maps.TravelMode | undefined>();

  const leg = route?.routes?.[0]?.legs?.[0];
  const startPos = leg?.start_location ? { lat: leg.start_location.lat(), lng: leg.start_location.lng() } : undefined;
  const endPos = leg?.end_location ? { lat: leg.end_location.lat(), lng: leg.end_location.lng() } : undefined;
  // get midpoint & drive time
  const routeInfo = useMemo(() => {
    if (!route) return null;

    // compute midpoint of overview path
    const path = route.routes[0]?.overview_path;
    if (!path) return null;
    const midpoint = path[Math.floor(path.length / 2)];

    return {
      midpoint,
      duration: leg.duration?.text ?? '',
      distance: leg.distance?.text ?? '',
      arrivalTime: leg.arrival_time?.text ?? null,
    };
  }, [route]);

  return (
    <div className="RouteMap">
      <Card className="d-flex flex-column gap-3 h-100">
        <CardHeader icon="sv sv-location" subtitle="Click 'View' on any route from your search history to display it on the map">
          Route Map & Directions
        </CardHeader>
        <Card.Body className="flex-grow-1 h-100 pt-0">
          {puAddress && doAddress && (
            <MapDisplay
              onLoad={() => {
                setTravelMode(window.google.maps.TravelMode.DRIVING);
              }}
            >
              {travelMode && (
                <>
                  <DirectionsService
                    options={{
                      destination: doAddress,
                      origin: puAddress,
                      travelMode,
                    }}
                    callback={(res) => {
                      if (!res || route) return;
                      setRoute(res);
                    }}
                  />
                  {route && (
                    <>
                      <DirectionsRenderer
                        options={{
                          directions: route,
                          suppressMarkers: true,
                          polylineOptions: {
                            strokeColor: Styles.VARIANT_MAPPING.secondary,
                            strokeWeight: 4,
                          },
                        }}
                      />
                      {startPos && <MapMarker position={startPos} color={Styles.VARIANT_MAPPING.secondary} />}
                      {endPos && <MapMarker position={endPos} color={Styles.VARIANT_MAPPING.secondary} />}
                      {routeInfo && (
                        <InfoWindow position={routeInfo.midpoint}>
                          <div style={{ minWidth: '140px' }}>
                            <strong>Drive Time</strong>
                            <div>Duration: {routeInfo.duration}</div>
                            <div>Distance: {routeInfo.distance}</div>
                          </div>
                        </InfoWindow>
                      )}
                    </>
                  )}
                </>
              )}
            </MapDisplay>
          )}
          {(!puAddress || !doAddress) && (
            <div className="map-content">
              <div className="map-placeholder">
                <div className="map-icon-large">
                  <i className="fas fa-map"></i>
                </div>
                <h3 className="ready-title">Route Visualization Ready</h3>
                <p className="ready-description">Create a search query to view the route map.</p>
              </div>
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default React.memo(RouteMap);
